/**
 * Queue-lane cell pieces shared by the register and the legacy table
 * (SA-270 / SA-397): the state chip, assignee initials, waiting duration and
 * the priority "why" popover. Moved out of OrdersPage unchanged.
 */
import { useState } from 'react'
import { Badge } from '@/components/ui'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover'
import { QUEUE_STATE_CONFIG, WAITING_ON_LABELS } from '@/constants/queueStates'
import { firstQuoteState } from './firstQuote'
import { FirstQuoteDuration } from './FirstQuoteDuration'
import { QUEUE_SENT, QUEUE_WAITING } from './firstQuoteText'
import { assignedToTitle } from './ownerText'
import { initialsFor, waitingDuration } from './queueText'
import type { Order, PriorityFactor, QueueState } from '@/types/commerce'

export function QueueChip({ queueState }: { queueState: QueueState }) {
  const cfg = QUEUE_STATE_CONFIG[queueState]
  if (!cfg) return null
  // SA-468: the one chip primitive; the queue state's accent wash rides `soft`.
  return <Badge variant="soft" className={cfg.className}>{cfg.label}</Badge>
}

/** "date_proximity" -> "Date proximity" (spec §5.2/§7 wording). */
function humanizeFactor(factor: string): string {
  const spaced = factor.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/** Priority "why" breakdown (spec §5.2/§7) — only present under
 * `sort=priority`; a popover keeps the row compact while surfacing the
 * per-factor math behind the score. */
export function PriorityBreakdown({ priority }: { priority: { score: number; breakdown: PriorityFactor[] } }) {
  const [open, setOpen] = useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={e => e.stopPropagation()}
          className="text-xs text-muted-foreground hover:text-foreground underline decoration-dotted underline-offset-2 text-left"
        >
          Priority {priority.score.toFixed(2)}
        </button>
      </PopoverTrigger>
      <PopoverContent onClick={e => e.stopPropagation()} className="w-72 p-3 space-y-2">
        <p className="text-sm font-semibold text-foreground">Priority score: {priority.score.toFixed(2)}</p>
        <ul className="space-y-1">
          {priority.breakdown.map(f => (
            <li key={f.factor} className="flex items-baseline justify-between gap-3 text-xs">
              <span className="text-foreground">{humanizeFactor(f.factor)}</span>
              <span className="text-muted-foreground shrink-0">
                {f.unavailable
                  ? // SA-287: an unavailable factor can carry no weight —
                    // "weight null · unavailable" helps nobody.
                    f.weight == null
                    ? 'unavailable'
                    : `weight ${f.weight} · unavailable`
                  : `weight ${f.weight} · +${f.contribution}`}
              </span>
            </li>
          ))}
        </ul>
      </PopoverContent>
    </Popover>
  )
}

/** The whole queue cell: chip, assignee initials, who it waits on, priority. */
export function QueueCell({ order, assigneeNames }: { order: Order; assigneeNames: Record<string, string> }) {
  if (!order.queueState) return null
  const firstQuote = firstQuoteState(order.firstQuote, order, new Date())
  return (
    <div className="flex flex-col gap-1 items-start">
      <div className="flex items-center gap-1.5">
        <QueueChip queueState={order.queueState} />
        {order.assignedTo && assigneeNames[order.assignedTo] && (
          <span
            title={assignedToTitle(assigneeNames[order.assignedTo])}
            className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-muted text-[10px] font-semibold text-muted-foreground"
          >
            {initialsFor(assigneeNames[order.assignedTo])}
          </span>
        )}
      </div>
      {order.waitingOn && (
        <p className="text-[11.5px] text-muted-foreground">
          waiting {waitingDuration(order.queueStateEnteredAt)} on {WAITING_ON_LABELS[order.waitingOn].toLowerCase()}
        </p>
      )}
      {order.priority && <PriorityBreakdown priority={order.priority} />}
      {firstQuote && (
        <p className="text-[11.5px] text-muted-foreground">
          {firstQuote.kind === 'sent'
            ? <>{QUEUE_SENT} <FirstQuoteDuration {...firstQuote} tip="title" /></>
            : <>{QUEUE_WAITING} · <FirstQuoteDuration {...firstQuote} tip="title" /></>}
        </p>
      )}
    </div>
  )
}
